import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../../common/Dialog';
import { fetchVisitHistory } from '../../../../services/patientmr/visits.service';
import { AuthorizationsService } from '../../../../services/patientmr/authorizations.service';
import type { Visit } from '../../../../dtos/patientmr/visit.dto';
import { describeApiError } from '../../../../utils/apiErrors';

/** Visits that can be retro-linked (PA-08). The backend has no COMPLETED status; it's accepted defensively. */
const LINKABLE_STATUSES = new Set(['COMPLETED', 'READYFORBILLING']);

interface LinkVisitsDialogProps {
  open: boolean;
  patientId: string;
  authId: string;
  /** Visits already linked to this PA (hidden from the list) */
  linkedVisitIds: string[];
  /** Approved window (YYYY-MM-DD); visits outside it are hidden */
  approvedStartDate?: string | null;
  approvedEndDate?: string | null;
  onClose: () => void;
  /** Called after each successful link so the tracker can refresh usage */
  onLinked: () => void;
}

function visitDate(v: Visit): string {
  return (v.visitDate || v.startTime || '').slice(0, 10);
}

export const LinkVisitsDialog: React.FC<LinkVisitsDialogProps> = ({
  open, patientId, authId, linkedVisitIds, approvedStartDate, approvedEndDate, onClose, onLinked,
}) => {
  const titleId = useId();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchVisitHistory(patientId, 100, undefined, {
      startDate: approvedStartDate || undefined,
      endDate: approvedEndDate || undefined,
    })
      .then(res => {
        if (cancelled) return;
        const linked = new Set(linkedVisitIds);
        const eligible = res.visits.filter(v => {
          const id = v.id || v.visitId || '';
          const date = visitDate(v);
          if (!id || linked.has(id)) return false;
          if (!LINKABLE_STATUSES.has(String(v.status || '').toUpperCase())) return false;
          if (approvedStartDate && date && date < approvedStartDate) return false;
          if (approvedEndDate && date && date > approvedEndDate) return false;
          return true;
        });
        setVisits(eligible);
      })
      .catch(err => { if (!cancelled) setError(describeApiError(err, 'Failed to load visits.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, patientId, approvedStartDate, approvedEndDate, linkedVisitIds]);

  const handleLink = async (visit: Visit) => {
    const id = visit.id || visit.visitId || '';
    if (!id) return;
    setLinkingId(id);
    setError(null);
    try {
      await AuthorizationsService.linkVisit(patientId, authId, id);
      setVisits(prev => prev.filter(v => (v.id || v.visitId) !== id));
      onLinked();
    } catch (err) {
      setError(describeApiError(err, 'Failed to link the visit.'));
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={() => { if (!linkingId) onClose(); }} labelledBy={titleId}>
      <div className="text-left">
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">Link visits</h3>
        <p className="mt-1 text-sm text-slate-600">
          Completed visits in the approved dates that aren&apos;t linked to this PA. Linking counts the visit&apos;s units.
        </p>
        {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
        <div className="mt-3 max-h-80 overflow-y-auto border border-slate-200 rounded-md">
          {loading ? (
            <p className="p-3 text-sm text-slate-500">Loading visits...</p>
          ) : visits.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">No visits to link.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visits.map(v => {
                const id = v.id || v.visitId || '';
                return (
                  <li key={id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800">{visitDate(v) || '—'}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {[v.providerName, v.discipline, v.status].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleLink(v)}
                      disabled={linkingId !== null}
                      className="min-h-[44px] sm:min-h-0 shrink-0 px-3 py-1 text-xs font-medium rounded-md bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      {linkingId === id ? 'Linking...' : 'Link'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={linkingId !== null}
            className="min-h-[44px] px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Done
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default LinkVisitsDialog;
